import { useOne } from "@refinedev/core";
import { MediaAsset } from "pages/media/types";
import { useMemo } from "react";
import {
  ASSET_REFRESH_INTERVAL,
  ASSET_STALE_TIME,
} from "pages/media/constants";

export const useRefreshAssetPath = ({
  assetId,
  path,
  autoRefresh = false,
  placeholderAssets,
}: {
  assetId?: string;
  path?: string;
  autoRefresh?: boolean;
  placeholderAssets?: Record<string, string>;
}) => {
  const placeholderPath = assetId ? placeholderAssets?.[assetId] : undefined;
  const effectivePath = path ?? placeholderPath;

  const {
    data: asset,
    isLoading,
    refetch: refreshAssetUrl,
  } = useOne<MediaAsset>({
    resource: `media/media/assets`,
    id: assetId,
    errorNotification: false,
    queryOptions: {
      enabled: Boolean(!effectivePath && assetId),
      staleTime: ASSET_STALE_TIME,
      refetchInterval: autoRefresh ? ASSET_REFRESH_INTERVAL : false,
    },
  });

  const assetToUse = useMemo(() => {
    if (effectivePath) return undefined;
    // Prevent stale data from being used
    if (asset?.data?.id === assetId) return asset?.data;
    return undefined;
  }, [effectivePath, asset, assetId]);

  const currentPath = assetToUse?.path ?? effectivePath;

  return {
    asset: assetToUse,
    isLoading,
    refreshAssetUrl,
    currentPath,
  };
};
